import * as React from 'react';
import PropTypes from 'prop-types';
import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import DeleteIcon from '@mui/icons-material/Delete';
import FilterListIcon from '@mui/icons-material/FilterList';
import { visuallyHidden } from '@mui/utils';

import CustomPagination from '../components/CustomPagination';
import theme from '../../../assets/theme';


function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function EnhancedTableHead(props) {
  const { onSelectAllClick, order, orderBy, numSelected, rowCount, onRequestSort } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  

  return (
    <TableHead>
      <TableRow
        sx={{
          display: 'flex', // Use Flexbox
          backgroundColor: theme.palette.secondary.main,
          borderRadius: "8px",
          "& th:first-of-type": {
            borderBottomLeftRadius: "8px",
            borderTopLeftRadius: "8px"
          },
          "& th:last-of-type": {
            borderBottomRightRadius: "8px",
            borderTopRightRadius: "8px"
          },
        }}
      >
        {props.headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.align}
            padding={headCell.padding ? headCell.padding : "none"}
            sortDirection={orderBy === headCell.id ? order : false}
            sx={{
              flex: headCell.flex || 1, // Apply flex value
              padding: '16px',
              borderBottom: "none",
              justifyContent: headCell.align || "center",
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {headCell.canSort ? (
              <TableSortLabel
                active={orderBy === headCell.id}
                direction={orderBy === headCell.id ? order : 'asc'}
                onClick={createSortHandler(headCell.id)}
              >
                <Typography noWrap fontWeight={"500"} fontSize={14}>{headCell.label}</Typography>
                {orderBy === headCell.id ? (
                  <Box component="span" sx={visuallyHidden}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            ) : (
              <Typography noWrap sx={{
                fontWeight: 700, // Font weight 700
                fontSize: '14px', // Font size 14px
                fontFamily: 'Nunito Sans', // Font family 'Nunito Sans'
              }}>{headCell.label}</Typography>
            )}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}





EnhancedTableHead.propTypes = {
  numSelected: PropTypes.number.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  onSelectAllClick: PropTypes.func.isRequired,
  order: PropTypes.oneOf(['asc', 'desc']).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
};


export default function CommonTable({ data, onRowClick, disableElevation, search = "", backgroundColor, columnSearch = {}, paginate = true }) {
  const [order, setOrder] = React.useState('asc');
  const [orderBy, setOrderBy] = React.useState(null);
  const [selected, setSelected] = React.useState([]);
  const [totalItems, setTotalItems] = React.useState(0);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);
  const [searchQuery, setSearchQuery] = React.useState(search);
  const [searchArguments, setSearchArguments] = React.useState(columnSearch);
  const [elepadding, setElePadding] = React.useState(8);



  React.useEffect(() => {
    console.log("Checking the change", data.columns.length, totalItems);
    if (data.columns.length != totalItems) {
      setPage(0);
      setTotalItems(data.columns.length);
      if (!paginate) {
        setRowsPerPage(data.columns.length);
      }
    }
  }, [data, setTotalItems, setPage]);

  // const [page, setPage] = React.useState(0);
  // const [rowsPerPage, setRowsPerPage] = React.useState(5);

  // Load saved rowsPerPage and page from localStorage on component mount
  React.useEffect(() => {
    const savedRowsPerPage = localStorage.getItem("rowsPerPage");
    const savedPage = localStorage.getItem("page");

    if (savedRowsPerPage) {
      setRowsPerPage(parseInt(savedRowsPerPage, 10));
    }
    if (savedPage) {
      setPage(parseInt(savedPage, 10));
    }
  }, []);

  const handleRowsPerPageChange = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    localStorage.setItem("rowsPerPage", newRowsPerPage); // Store the selected value
  };

  const handleChangePage = (newPage) => {
    setPage(newPage);
    localStorage.setItem("page", newPage); // Store the selected page
  };


  //



  React.useEffect(() => {
    if (Object.values(columnSearch).length > 0) {
      setSearchArguments(columnSearch);
      setPage(0);
    }
  }, [columnSearch, setSearchArguments, setPage]);

  React.useEffect(() => {
    setSearchQuery(search);
    setPage(0);
  }, [search]);

  const handleClick = (event, id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }
    setSelected(newSelected);
  };

  function EnhancedTableColumns({ rows, columns, onRowClick, page, rowsPerPage }) {
    return columns.map((cel, cind) => (
      <TableRow
        hover
        onClick={() => {
          const dataIndex = page * rowsPerPage + cind; // Calculate the data index
          console.log("Clicked row data index:", dataIndex, cel);
          onRowClick ? onRowClick(cel, dataIndex) : console.log("Row clicked with index:", dataIndex);
        }}
        key={cel.id ? cel.id : cind.toString()}
        style={{ height: cel['height'] ?? 40 }}
        sx={{ display: 'flex', cursor: 'pointer' }}
      >
        {rows.map((ele, ind) => (
          <TableCell
            align={"center"}
            key={ind}
            sx={{
              backgroundColor: backgroundColor,
              flex: ele.flex || 1, // Use the flex value from the corresponding headCell
              padding: '16px',
              display: 'flex',
              alignItems: 'center',
              textAlign: ele.align || "center",
              justifyContent: ele.align || "center",
              borderBottom: cel['borderBottom'] ? cel['borderBottom'] : "1px solid rgba(224, 224, 224, 1)"
            }}>
            {cel[ele.accessor] || ''}
          </TableCell>
        ))}
      </TableRow>
    ));
  }


  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelected = data.rows.map((n) => n.id);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  // const handleChangePage = (newPage) => {
  //   setPage(newPage); // This keeps `page` in sync with the child component.
  // };


  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const searchList = () => {
    let temp = [...data.columns];
    if ((data.columns.length > 0) && data.columns[0].searchkey) {
      temp = [...data.columns]
        .filter(item =>
          item.searchkey?.toLowerCase().includes(searchQuery.toLowerCase()))
    }
    if (Object.keys(searchArguments).length > 0) {
      for (let ks of Object.keys(searchArguments)) {
        temp = [...temp].filter(item =>
          item[ks]?.toLowerCase().includes(searchArguments[ks].toLowerCase()))
      }
    }
    return temp;
  }



  const visibleRows = React.useMemo(
    () =>
      searchList()
        .sort(getComparator(order, orderBy))
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [data.columns, order, orderBy, page, rowsPerPage, searchQuery, searchArguments],
  );

  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - searchList().length) : 0;

  return (
    <Box sx={{ width: '100%' }}>
      <Paper elevation={disableElevation ? 0 : 2} sx={{ mb: 2, borderRadius: disableElevation ? 0 : 2, overflow: "hidden", width: '100%', backgroundColor: "orange" }}>
        <TableContainer sx={{ width: '100%', overflow: 'hidden' }} >
          <Table aria-labelledby="tableTitle">
            <EnhancedTableHead
              numSelected={selected.length}
              order={order}
              orderBy={orderBy}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
              rowCount={data.columns.length}
              headCells={data.rows}
            />
            <TableBody>
              {visibleRows.length > 0 ? (
                <EnhancedTableColumns
                  rows={data.rows}
                  columns={visibleRows}
                  page={page}
                  rowsPerPage={rowsPerPage}
                  onRowClick={onRowClick}
                />
              ) : (
                <TableRow>
                  <TableCell colSpan={data.rows.length} align="center" sx={{ backgroundColor: backgroundColor }}>
                    <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '14px', fontFamily: 'Nunito Sans' }}>
                      No data found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {/* Empty rows to maintain consistent height */}
              {(emptyRows > 0) && (paginate) && (
                <TableRow
                  style={{
                    height: (40) * emptyRows,
                  }
                  }
                >
                  <TableCell colSpan={6} sx={{ backgroundColor: backgroundColor }} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      {paginate && <CustomPagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={searchList().length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleRowsPerPageChange}  // Pass the function

      />}
    </Box>
  );
}
